import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO, { getBreadcrumbSchema, getFAQSchema } from '../components/SEO';
import { MapPin, Briefcase, Package, Hammer, ArrowRight, BellRing, Plus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEME = {
  'praca-budowlana': {
    accent: 'violet', icon: Briefcase, kind: 'job',
    heading: (c) => `Praca budowlana ${c.name}`,
    seoTitle: (c) => `Praca budowlana ${c.name} - oferty pracy w budownictwie`,
    seoDesc: (c, n) => `Aktualne oferty pracy w budownictwie ${c.locative}${n ? ` i okolicy - ${n} ogłoszeń` : ''}. Murarz, elektryk, hydraulik, dekarz, operator maszyn. Aplikuj bezpłatnie na Giełdzie Budowlanej.`,
    cta: { label: 'Włącz alerty o nowych ofertach', to: '/profile', icon: BellRing },
  },
  remonty: {
    accent: 'orange', icon: Hammer, kind: 'offer',
    heading: (c) => `Remonty ${c.name} - zlecenia budowlane`,
    seoTitle: (c) => `Remonty ${c.name} - zlecenia i wykonawcy budowlani`,
    seoDesc: (c, n) => `Zlecenia remontowe i budowlane ${c.locative}${n ? ` - ${n} aktywnych ogłoszeń` : ''}. Dodaj zlecenie bezpłatnie i porównaj oferty sprawdzonych firm z okolicy.`,
    cta: { label: 'Dodaj zlecenie za darmo', to: '/offer-choice', icon: Plus },
  },
  'materialy-budowlane': {
    accent: 'emerald', icon: Package, kind: 'material',
    heading: (c) => `Materiały budowlane ${c.name}`,
    seoTitle: (c) => `Materiały budowlane ${c.name} - giełda materiałów`,
    seoDesc: (c, n) => `Kup i sprzedaj materiały budowlane ${c.locative}${n ? ` - ${n} ogłoszeń` : ''}. Cement, cegły, drewno, izolacje, dachy, narzędzia - nowe i używane, bez prowizji.`,
    cta: { label: 'Wystaw materiały', to: '/materials/new', icon: Plus },
  },
};

const ACCENT = {
  violet: { bg: 'from-violet-600 to-purple-700', text: 'text-violet-700', chip: 'bg-violet-50 text-violet-700', btn: 'bg-violet-600 hover:bg-violet-700' },
  orange: { bg: 'from-orange-500 to-amber-600', text: 'text-orange-700', chip: 'bg-orange-50 text-orange-700', btn: 'bg-orange-500 hover:bg-orange-600' },
  emerald: { bg: 'from-emerald-600 to-teal-700', text: 'text-emerald-700', chip: 'bg-emerald-50 text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700' },
};

const formatMoney = (n) => new Intl.NumberFormat('pl-PL').format(Number(n || 0));

const CityLanding = ({ vertical }) => {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = THEME[vertical];
  const accent = ACCENT[theme.accent];
  const Icon = theme.icon;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/seo/city/${vertical}/${citySlug}`);
        setData(res.ok ? await res.json() : null);
      } catch (e) {
        console.error('City page error:', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [vertical, citySlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4 px-4 text-center" data-testid="city-page-not-found">
        <h1 className="font-heading font-bold text-2xl text-slate-800">Nie znaleźliśmy takiej strony</h1>
        <Link to="/"><Button variant="outline">Wróć na stronę główną</Button></Link>
      </div>
    );
  }

  const { city, labels, items, categories, nearby, faq, total_local, total_nearby } = data;
  const totalAll = total_local + total_nearby;
  const activeCategories = categories.filter((c) => c.count > 0).slice(0, 8);
  const canonical = `/${vertical}/${citySlug}`;

  const schema = [
    getBreadcrumbSchema([
      { name: 'Strona główna', path: '/' },
      { name: labels.title, path: labels.path },
      { name: city.name, path: canonical },
    ]),
    getFAQSchema(faq.map((f) => ({ question: f.q, answer: f.a }))),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="city-landing-page">
      <SEO
        title={theme.seoTitle(city)}
        description={theme.seoDesc(city, totalAll)}
        canonical={canonical}
        structuredData={schema}
      />

      <div className={`bg-gradient-to-r ${accent.bg} py-14`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-white/70 mb-6 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-white">Strona główna</Link>
            <span>/</span>
            <Link to={labels.path} className="hover:text-white">{labels.title}</Link>
            <span>/</span>
            <span className="text-white">{city.name}</span>
          </nav>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white" data-testid="city-page-h1">
                {theme.heading(city)}
              </h1>
              <p className="text-white/80 mt-2 text-base">
                {city.name}, woj. {city.voivodeship} — {totalAll > 0 ? `${totalAll} ${labels.plural}` : `brak ${labels.plural} w tej chwili`}
              </p>
            </div>
          </div>
          <Link to={theme.cta.to}>
            <Button className={`mt-6 bg-white ${accent.text} hover:bg-white/90 font-semibold`} data-testid="city-page-cta">
              <theme.cta.icon className="w-4 h-4 mr-2" />{theme.cta.label}
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        <section className="prose-slate max-w-3xl">
          <p className="text-slate-700 leading-relaxed">
            {vertical === 'praca-budowlana' && (
              <>Szukasz pracy w budownictwie {city.locative}? Na Giełdzie Pracy zbieramy ogłoszenia firm budowlanych
              {' '}{city.locative} i okolicy (woj. {city.voivodeship}). Aplikowanie jest darmowe, a pracodawca kontaktuje się
              z Tobą bezpośrednio — bez agencji i bez prowizji. Jeśli nie chcesz codziennie sprawdzać nowych ofert, włącz
              w profilu darmowe alerty e-mail i dostaniesz wiadomość, gdy pojawi się praca w promieniu do 70 km od Twojej miejscowości.</>
            )}
            {vertical === 'remonty' && (
              <>Planujesz remont {city.locative}? Opisz zakres prac, a firmy budowlane {city.locative} i okolicy same wyślą
              Ci swoje oferty. Dodanie zlecenia jest bezpłatne, nie pobieramy prowizji od wykonawców ani od klientów.
              Każdą firmę sprawdzisz przed decyzją — profil, galeria realizacji i opinie innych osób z okolicy (woj. {city.voivodeship}).</>
            )}
            {vertical === 'materialy-budowlane' && (
              <>Giełda Materiałów to miejsce, gdzie kupisz i sprzedasz materiały budowlane {city.locative} —
              nadwyżki z budów, końcówki serii i materiały z rozbiórek. Cement, cegły, drewno, izolacje, pokrycia dachowe
              czy narzędzia znajdziesz taniej niż w markecie, a odbiór ustalasz bezpośrednio ze sprzedającym {city.locative}
              {' '}lub w okolicy.</>
            )}
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-lg md:text-lg text-slate-900 mb-4">
            {totalAll > 0 ? `Aktualne ogłoszenia — ${city.name} i okolice` : `Brak ogłoszeń — ${city.name}`}
          </h2>
          {items.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-600">
                <p className="mb-4">Nie ma teraz {labels.plural} {city.locative}. Zajrzyj za kilka dni lub sprawdź sąsiednie miasta poniżej.</p>
                <Link to={labels.path}><Button variant="outline">Zobacz wszystkie ogłoszenia</Button></Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="city-page-items">
              {items.map((item) => (
                <Link key={item.id} to={`${labels.path}/${item.id}`} data-testid={`city-item-${item.id}`}>
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5 flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-heading font-semibold text-slate-900">{item.title}</h3>
                          {item.is_nearby && <Badge variant="outline" className="text-xs">okolica</Badge>}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-2">{item.description}</p>
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="w-4 h-4" />{item.location}
                        </span>
                      </div>
                      <div className={`text-right shrink-0 font-semibold ${accent.text}`}>
                        {theme.kind === 'job' && `${formatMoney(item.salary_min)} PLN`}
                        {theme.kind === 'material' && `${formatMoney(item.price)} PLN`}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>

        {activeCategories.length > 0 && (
          <section>
            <h2 className="font-heading font-bold text-lg md:text-lg text-slate-900 mb-4">
              Popularne kategorie — {city.name}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="city-page-categories">
              {activeCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`${labels.path}?category=${cat.id}&location=${encodeURIComponent(city.name)}`}
                  className="rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-400 transition-colors"
                >
                  <span className="block font-medium text-slate-800 text-sm">{cat.name}</span>
                  <span className={`text-xs ${accent.text}`}>{cat.count} ogł.</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-heading font-bold text-lg md:text-lg text-slate-900 mb-4">Sprawdź też w pobliżu</h2>
          <div className="flex flex-wrap gap-2" data-testid="city-page-nearby">
            {nearby.map((n) => (
              <Link
                key={n.slug}
                to={`/${vertical}/${n.slug}`}
                className={`rounded-full px-4 py-2 text-sm ${accent.chip} hover:opacity-80 transition-opacity`}
              >
                {n.name} <span className="opacity-60">{n.distance_km} km</span>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading font-bold text-lg md:text-lg text-slate-900 mb-4">
            Wszystko dla budownictwa {city.locative}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.keys(THEME).filter((v) => v !== vertical).map((v) => (
              <Link key={v} to={`/${v}/${citySlug}`} className="rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-400 transition-colors flex items-center justify-between">
                <span className="font-medium text-slate-800 text-sm">{THEME[v].heading({ name: city.name, locative: city.locative })}</span>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading font-bold text-lg md:text-lg text-slate-900 mb-4">
            Najczęstsze pytania — {city.name}
          </h2>
          <Accordion type="single" collapsible className="bg-white rounded-lg border border-slate-200 px-4" data-testid="city-page-faq">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>
    </div>
  );
};

export default CityLanding;
